import React from 'react';
import { observable, action, makeObservable } from "mobx";
import { Auth, Client, User, Brief, Project, ProjectPlan, Evaluation, Quote,Voucher } from '../services/requests';
import pako from 'pako';
//import utils from '../../../credipple_middleware/src/shared/utils';


export default class Store {
  // #region Observables
  @observable isLoggedIn = false;
  @observable currentPage = ''
  @observable viewSize = 0;
  @observable screenWidth = window.innerWidth;
  @observable screenHeight = window.innerHeight;
  @observable isLoading = false;
  @observable screenMobile = (window.innerWidth < 700)
  // #endregion

  //Loading
  @action setLoading(val) {
    this.isLoading = val;
  }

  // #region Constructor
  constructor() {
    makeObservable(this);
  }
  // #endregion

  // #region Webauthn Helpers
  @action async preformatGetAssertReq(getAssert) {
    getAssert.challenge = await this.decode(getAssert.challenge);

    for (let allowCred of getAssert.allowCredentials) {
      allowCred.id = await this.decode(allowCred.id);
    }

    return getAssert
  }
  @action async publicKeyCredentialToJSON(pubKeyCred) {
    if (pubKeyCred instanceof Array) {
      let arr = [];
      for (let i of pubKeyCred)
        arr.push(await this.publicKeyCredentialToJSON(i));

      return arr
    }

    if (pubKeyCred instanceof ArrayBuffer) {
      return await this.encode(pubKeyCred)
    }

    if (pubKeyCred instanceof Object) {
      let obj = {};

      for (let key in pubKeyCred) {
        obj[key] = await this.publicKeyCredentialToJSON(pubKeyCred[key])
      }

      return obj
    }

    return pubKeyCred
  }
  @action async encode(arraybuffer) {
    let chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

    // Use a lookup table to find the index.
    let lookup = new Uint8Array(256);
    for (let i = 0; i < chars.length; i++) {
      lookup[chars.charCodeAt(i)] = i;
    }
    let bytes = new Uint8Array(arraybuffer),
      i, len = bytes.length, base64url = '';

    for (i = 0; i < len; i += 3) {
      base64url += chars[bytes[i] >> 2];
      base64url += chars[((bytes[i] & 3) << 4) | (bytes[i + 1] >> 4)];
      base64url += chars[((bytes[i + 1] & 15) << 2) | (bytes[i + 2] >> 6)];
      base64url += chars[bytes[i + 2] & 63];
    }

    if ((len % 3) === 2) {
      base64url = base64url.substring(0, base64url.length - 1);
    } else if (len % 3 === 1) {
      base64url = base64url.substring(0, base64url.length - 2);
    }

    return base64url;
  }
  @action async decode(base64string) {
    let chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

    // Use a lookup table to find the index.
    let lookup = new Uint8Array(256);
    for (let i = 0; i < chars.length; i++) {
      lookup[chars.charCodeAt(i)] = i;
    }
    let bufferLength = base64string.length * 0.75,
      len = base64string.length, i, p = 0,
      encoded1, encoded2, encoded3, encoded4;

    let bytes = new Uint8Array(bufferLength);

    for (i = 0; i < len; i += 4) {
      encoded1 = lookup[base64string.charCodeAt(i)];
      encoded2 = lookup[base64string.charCodeAt(i + 1)];
      encoded3 = lookup[base64string.charCodeAt(i + 2)];
      encoded4 = lookup[base64string.charCodeAt(i + 3)];

      bytes[p++] = (encoded1 << 2) | (encoded2 >> 4);
      bytes[p++] = ((encoded2 & 15) << 4) | (encoded3 >> 2);
      bytes[p++] = ((encoded3 & 3) << 6) | (encoded4 & 63);
    }

    return bytes.buffer
  }
  @action async preformatMakeCredReq(makeCredReq) {
    makeCredReq.challenge = await this.decode(makeCredReq.challenge);
    makeCredReq.user.id = await this.decode(makeCredReq.user.id);

    return makeCredReq
  }
  // #endregion

  // #region Add webauthns
  @action async addWebauthn() {
    let retObj = await Auth.addPublicKey(this.user._id);
    if (retObj.data === 'error') {
      return retObj.data;
    }

    let challengeMakeCred = retObj.data;

    let publicKey = await this.preformatMakeCredReq(challengeMakeCred);

    let nav = await navigator.credentials.create({ publicKey }).then((success) => {
      return success
    }).catch((error) => {
      return false
    });

    if (!nav) {
      return false
    }

    let makeCredResponse = await this.publicKeyCredentialToJSON(nav);
    if (!makeCredResponse.rawId || !makeCredResponse.type || !makeCredResponse.id || makeCredResponse.type !== "public-key") {
      return false
    }

    let args = {
      challenge: makeCredResponse,
      Device: navigator.userAgentData
    };
    retObj = await Auth.saveWebAuthn(this.user._id, args);
    if (!retObj.data.success) {
      return false
    }
    this.user = retObj.data.user;
    return true
  }
  // #endregion

  // #region User
  @action async createUser(data) {
    let retObj = await User.createUser(data);
    return retObj.data.response.user
  }
  @action async getUserById(id) {
    let retObj = await User.getUserById(id);
    return retObj.data.response.user
  }
  @action async getUserByEmail(email) {
    let retObj = await User.getUserByEmail(email)
    return retObj.data.user
  }

  @action async getUsers() {
    try {
      let allUsers = [];
      let currentPage = 1;
      let hasNextPage = true;
      const pageSize = 450; // Using the same page size as backend

      while (hasNextPage) {
        const response = await User.getUsers(currentPage, pageSize);

        if (response.data && response.data.isBase64Encoded) {
          const base64Data = response.data.body;
          const binaryData = atob(base64Data);

          const uint8Array = new Uint8Array(binaryData.length);
          for (let i = 0; i < binaryData.length; i++) {
            uint8Array[i] = binaryData.charCodeAt(i);
          }

          const decompressedData = pako.inflate(uint8Array, { to: 'string' });
          const jsonData = JSON.parse(decompressedData);
          allUsers = [...allUsers, ...jsonData.response.users];
          hasNextPage = jsonData.response.pagination.hasNextPage;
        } else {
          const users = response.data.response.users;
          allUsers = [...allUsers, ...users];
          hasNextPage = response.data.response.pagination.hasNextPage;
        }
        currentPage++;
      }

      return allUsers;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  @action async updateUserById(id, data) {
    let retObj = await User.updateUserById(id, data)
    return retObj.data.response.user
  }
  @action async updatePassword(email, password) {
    let retObj = await User.updatePassword(email, password)
    return retObj.data.response.user
  }
  @action async syncData() {
    let retObj = await User.syncData()
    return retObj.data.value
  }
  @action async updateUserByEmail(data, email) {
    let retObj = await User.updateUserByEmail(data, email)
    return retObj.data.response.user
  }
  @action async addProfile() {
    let retObj = await User.addProfile();
    return retObj.data.response.profile;
  }
  // #endregion

  // #region Client
  @action async getClients() {
    let retObj = await Client.getClients();
    return retObj.data.response.clients
  }
  @action async createClient(data) {
    let retObj = await Client.createClient(data);
    return retObj.data.response.client
  }
  @action async getClientById(id) {
    let retObj = await Client.getClientById(id);
    return retObj.data.response.client
  }
  @action async getClientByEmail(email) {
    let retObj = await Client.getClientByEmail(email);
    return retObj.data.response.clients
  }
  @action async updateClientById(id, data) {
    let retObj = await Client.updateClientById(id, data)
    return retObj.data.response.client
  }
  @action async updateClientByEmail(data, email) {
    let retObj = await Client.updateClientByEmail(data, email)
    return retObj
  }
  // #endregion

  // #region ProjectPlan
  @action async getProjectPlans() {
    let retObj = await ProjectPlan.getProjectPlans();
    return retObj.data.response.projectPlans
  }
  @action async createProjectPlan(data) {
    let retObj = await ProjectPlan.createProjectPlan(data);
    return retObj.data.response.projectPlan
  }
  @action async getProjectPlanById(id) {
    let retObj = await ProjectPlan.getProjectPlanById(id);
    return retObj.data.response.projectPlan
  }
  @action async getProjectPlanByEmail(email) {
    let retObj = await ProjectPlan.getProjectPlanByEmail(email);
    return retObj.data.response.projectPlans
  }
  @action async updateProjectPlanById(id, data) {
    let retObj = await ProjectPlan.updateProjectPlanById(id, data)
    return retObj.data.response.projectPlan
  }
  @action async updateProjectPlanByEmail(data, email) {
    let retObj = await ProjectPlan.updateProjectPlanByEmail(data, email)
    return retObj
  }
  // #endregion

  // #region Brief
  @action async createBrief(data) {
    let retObj = await Brief.createBrief(data);
    return retObj.data.response.brief
  }
  @action async getBriefs() {
    let retObj = await Brief.getBriefs();
    return retObj.data.response.briefs
  }
  @action async getBriefByClient(id) {
    let retObj = await Brief.getBriefByClient(id);
    return retObj.data.response.briefs
  }
  @action async getBriefByProfessional(id) {
    let retObj = await Brief.getBriefByProfessional(id);
    return retObj.data.response.briefs
  }
  @action async updateBriefById(id, data) {
    let retObj = await Brief.updateBriefById(id, data)
    return retObj.data.response.brief
  }
  // #endregion

  // #region Project
  @action async getProjects() {
    let retObj = await Project.getProjects();
    return retObj.data.response.projects
  }
  @action async getProjectByClient(id) {
    let retObj = await Project.getProjectByClient(id);
    return retObj.data.response.projects
  }
  @action async getProjectByProfessional(id) {
    let retObj = await Project.getProjectByProfessional(id);
    return retObj.data.response.projects
  }
  @action async updateProjectById(id, data) {
    let retObj = await Project.updateProjectById(id, data)
    return retObj.data.response
  }
  // #endregion Project

  // #region evaluation
  @action async getEvaluation() {
    let retObj = await Evaluation.getEvaluations();
    return retObj.data.response.evaluations.data
  }
  @action async updateEvaluationById(id, data) {
    let retObj = await Evaluation.updateEvaluationById(id, data)
    return retObj.data.response.evaluation
  }
  // #endregion evaluation

  // #region quote
  @action async getQuote() {
    let retObj = await Quote.getQuotes();
    return retObj.data.response.quotes
  }
  @action async updateQuoteById(id, data) {
    let retObj = await Quote.updateQuoteById(id, data)
    return retObj.data.response.quote
  }

  // #endregion quote

    // #region voucher
    @action async getVouchers() {
      let retObj = await Voucher.getVouchers();
      return retObj.data.response.vouchers
    }
    // #endregion voucher
  
  @action logout() {
    window.localStorage.clear();
    this.isLoggedIn = false;
    this.user = {};
  }
  @action setJWT(token) {
    window.localStorage.setItem('jwt', token);
  }
  @action clearStores() {
    window.localStorage.clear();
    this.user = {};
  }
  @action async PasswordLogin(email, Password) {
    let retObj = await Auth.PasswordLogin(email, { Password: Password });
    this.setJWT(retObj.data.response.jwt)
    this.isLoggedIn = true;
    this.user = retObj.data.response.user;
    return true
  }
  @action setScreenWidth(width) {
    this.screenWidth = width;
  }
  @action setScreenHeight(height) {
    this.screenHeight = height;
  }
}

// #region Store Config
const StoreContext = React.createContext();

export const StoreProvider = ({ children, store }) => {
  return (
    <StoreContext.Provider value={store}>{children}</StoreContext.Provider>
  );
};

export const useStore = () => React.useContext(StoreContext);

export const withStore = (Component) => (props) => {
  return <Component {...props} store={useStore()} />;
};
// #endregion
